/**
 * /admin/previsionnel — Simulateur de rentabilité Cursus Physio.
 * Pure-frontend math, instant recalculation. Backend persists scenarios + PDF export.
 */
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Calculator, Save, FileDown, Settings as SettingsIcon, X, Sparkles,
    AlertTriangle, CheckCircle2, Scale, Users as UsersIcon, Trash2, ChevronRight,
    Layers, BarChart2, TrendingUp, Target, RefreshCw, Search,
} from "lucide-react";
import {
    LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
    BarChart, Bar, ReferenceLine, AreaChart, Area, Legend,
} from "recharts";
import { toast } from "sonner";
import { api, formatApiError, API } from "@/lib/api";

// ================ Helpers ================
const fmtEUR = (n) => {
    if (n === null || n === undefined || Number.isNaN(n)) return "—";
    const sign = n < 0 ? "− " : "";
    const v = Math.abs(Math.round(n));
    return sign + v.toLocaleString("fr-FR") + " €";
};
const fmtSigned = (n) => (n > 0 ? "+ " + fmtEUR(n) : fmtEUR(n));

// Centralised math (mirrored backend in routes_previsionnel.compute_session)
function computeSession({ students, price, costFormateur, costSalle, portagePct, capacity }) {
    const n = Math.max(0, Number(students) || 0);
    const p = Math.max(0, Number(price) || 0);
    const cf = Math.max(0, Number(costFormateur) || 0);
    const cs = Math.max(0, Number(costSalle) || 0);
    const portagePctSafe = Math.min(100, Math.max(0, Number(portagePct) || 0));
    const cap = Math.max(1, Number(capacity) || 20);
    const factor = 1 - portagePctSafe / 100;

    const ca_brut = n * p;
    const portage = ca_brut * portagePctSafe / 100;
    const ca_net = ca_brut - portage;
    const costs_total = cf + cs;
    const net_margin = ca_net - costs_total;
    const margin_per_student = n > 0 ? net_margin / n : 0;
    const break_even = (p > 0 && factor > 0) ? Math.ceil(costs_total / (p * factor)) : null;
    const full_capacity_margin = cap * p * factor - costs_total;
    return {
        students: n, price_per_student: p,
        ca_brut, portage, ca_net, costs_total,
        net_margin, margin_per_student, break_even,
        full_capacity_margin,
    };
}

// ================ Settings dialog ================
function SettingsDialog({ open, onClose, settings, onSaved }) {
    const [form, setForm] = useState(settings);
    const [saving, setSaving] = useState(false);
    useEffect(() => { setForm(settings); }, [settings]);
    if (!open) return null;
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { data } = await api.put("/admin/previsionnel/settings", {
                default_cost_formateur_per_day: Number(form.default_cost_formateur_per_day),
                default_cost_salle_per_day: Number(form.default_cost_salle_per_day),
                default_portage_percent: Number(form.default_portage_percent),
                default_room_capacity: Number(form.default_room_capacity),
            });
            toast.success("Paramètres enregistrés");
            onSaved(data);
            onClose();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/60 p-4" data-testid="prev-settings-dialog">
            <motion.form
                initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                onSubmit={submit}
                className="relative w-full max-w-lg rounded-3xl bg-white p-8 shadow-premium-lg"
            >
                <button type="button" onClick={onClose} className="absolute right-4 top-4 text-brand-smoke hover:text-brand-ink"><X className="h-5 w-5" /></button>
                <h3 className="font-display text-2xl">Paramètres prévisionnel</h3>
                <p className="mt-1 text-xs text-brand-smoke">Coûts par défaut utilisés dans toutes les simulations.</p>
                <div className="mt-6 grid gap-4">
                    <Input label="Coût formateur (€ / jour)" value={form.default_cost_formateur_per_day} onChange={(v) => setForm((f) => ({ ...f, default_cost_formateur_per_day: v }))} testId="prev-set-cost-formateur" />
                    <Input label="Coût salle + extras (€ / jour)" value={form.default_cost_salle_per_day} onChange={(v) => setForm((f) => ({ ...f, default_cost_salle_per_day: v }))} testId="prev-set-cost-salle" />
                    <Input label="% portage Axiome" value={form.default_portage_percent} onChange={(v) => setForm((f) => ({ ...f, default_portage_percent: v }))} suffix="%" testId="prev-set-portage" />
                    <Input label="Capacité max salle (stagiaires)" value={form.default_room_capacity} onChange={(v) => setForm((f) => ({ ...f, default_room_capacity: v }))} testId="prev-set-capacity" />
                </div>
                <div className="mt-6 flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="rounded-full border border-brand-line px-5 py-2 text-sm">Annuler</button>
                    <button type="submit" disabled={saving} data-testid="prev-set-save"
                        className="rounded-full bg-brand-orange px-5 py-2 text-sm font-semibold text-white">
                        {saving ? "Enregistrement…" : "Enregistrer les paramètres"}
                    </button>
                </div>
            </motion.form>
        </div>
    );
}

function Input({ label, value, onChange, suffix, testId, type = "number" }) {
    return (
        <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">{label}</span>
            <div className="mt-1 flex items-center gap-2 rounded-xl border border-brand-line bg-white px-3 py-2 focus-within:border-brand-orange">
                <input
                    type={type} value={value}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={testId}
                    className="flex-1 bg-transparent text-base outline-none"
                    step="any"
                />
                {suffix && <span className="text-sm text-brand-smoke">{suffix}</span>}
            </div>
        </label>
    );
}

function Slider({ label, value, min, max, step = 1, onChange, suffix = "", testId }) {
    return (
        <div>
            <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">{label}</span>
                <span className="font-display text-2xl font-medium tracking-tightest text-brand-orange">{value}{suffix}</span>
            </div>
            <input
                type="range" min={min} max={max} step={step} value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                data-testid={testId}
                className="mt-2 w-full accent-brand-orange"
            />
            <div className="mt-1 flex justify-between text-[10px] text-brand-smoke">
                <span>{min}{suffix}</span><span>{max}{suffix}</span>
            </div>
        </div>
    );
}

// ================ Save scenario dialog ================
function SaveScenarioDialog({ open, onClose, defaultName, onSubmit }) {
    const [name, setName] = useState(defaultName || "");
    const [description, setDescription] = useState("");
    const [tags, setTags] = useState("");
    const [saving, setSaving] = useState(false);
    useEffect(() => { setName(defaultName || ""); setDescription(""); setTags(""); }, [defaultName, open]);
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/60 p-4" data-testid="prev-save-dialog">
            <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                className="relative w-full max-w-md rounded-3xl bg-white p-8 shadow-premium-lg"
            >
                <button onClick={onClose} className="absolute right-4 top-4 text-brand-smoke"><X className="h-5 w-5" /></button>
                <h3 className="font-display text-2xl">Sauvegarder ce scénario</h3>
                <div className="mt-5 grid gap-4">
                    <label>
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">Nom *</span>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                            data-testid="prev-save-name"
                            className="mt-1 w-full rounded-xl border border-brand-line bg-white px-3 py-2 outline-none focus:border-brand-orange" />
                    </label>
                    <label>
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">Description</span>
                        <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
                            data-testid="prev-save-desc"
                            className="mt-1 w-full rounded-xl border border-brand-line bg-white px-3 py-2 outline-none focus:border-brand-orange" />
                    </label>
                    <label>
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">Tags (séparés par virgule)</span>
                        <input type="text" value={tags} onChange={(e) => setTags(e.target.value)}
                            data-testid="prev-save-tags"
                            placeholder="exemple : Q1-2026, lancement"
                            className="mt-1 w-full rounded-xl border border-brand-line bg-white px-3 py-2 outline-none focus:border-brand-orange" />
                    </label>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                    <button onClick={onClose} className="rounded-full border border-brand-line px-5 py-2 text-sm">Annuler</button>
                    <button
                        disabled={!name.trim() || saving}
                        data-testid="prev-save-submit"
                        onClick={async () => {
                            setSaving(true);
                            try {
                                await onSubmit({ name: name.trim(), description, tags: tags.split(",").map((t) => t.trim()).filter(Boolean) });
                                onClose();
                            } finally { setSaving(false); }
                        }}
                        className="rounded-full bg-brand-orange px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"
                    >
                        {saving ? "…" : "Sauvegarder"}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}

// ================ Sub-tab : Session unique ================
function SessionTab({ settings, formations, onSaveScenario }) {
    const [formationSlug, setFormationSlug] = useState("");
    const [students, setStudents] = useState(12);
    const [price, setPrice] = useState(390);
    const [costFormateur, setCostFormateur] = useState(settings.default_cost_formateur_per_day);
    const [costSalle, setCostSalle] = useState(settings.default_cost_salle_per_day);
    const [portagePct, setPortagePct] = useState(settings.default_portage_percent);
    const capacity = settings.default_room_capacity;
    const [saveOpen, setSaveOpen] = useState(false);

    useEffect(() => {
        setCostFormateur(settings.default_cost_formateur_per_day);
        setCostSalle(settings.default_cost_salle_per_day);
        setPortagePct(settings.default_portage_percent);
    }, [settings]);

    useEffect(() => {
        if (!formationSlug) return;
        const f = formations.find((x) => x.slug === formationSlug);
        if (f && f.price_eur) setPrice(f.price_eur);
    }, [formationSlug, formations]);

    const out = useMemo(() => computeSession({ students, price, costFormateur, costSalle, portagePct, capacity }),
        [students, price, costFormateur, costSalle, portagePct, capacity]);

    // 3 scénarios automatiques (basés sur capacity)
    const scenarios = useMemo(() => {
        const filling = [
            { key: "pess", label: "Pessimiste", color: "#dc2626", pct: 35 },
            { key: "real", label: "Réaliste", color: "#E8873C", pct: 60 },
            { key: "opt", label: "Optimiste", color: "#16a34a", pct: 95 },
        ];
        return filling.map((f) => {
            const s = Math.max(1, Math.round(capacity * f.pct / 100));
            const r = computeSession({ students: s, price, costFormateur, costSalle, portagePct, capacity });
            return { ...f, students: s, ...r };
        });
    }, [capacity, price, costFormateur, costSalle, portagePct]);

    // Marge selon nombre de stagiaires (graph)
    const marginCurve = useMemo(() => {
        const data = [];
        for (let i = 1; i <= capacity; i++) {
            const r = computeSession({ students: i, price, costFormateur, costSalle, portagePct, capacity });
            data.push({ students: i, margin: Math.round(r.net_margin) });
        }
        return data;
    }, [capacity, price, costFormateur, costSalle, portagePct]);

    const status = out.net_margin > 50 ? "ok" : out.net_margin >= -50 ? "even" : "ko";

    const reset = () => {
        setStudents(12); setPrice(390);
        setCostFormateur(settings.default_cost_formateur_per_day);
        setCostSalle(settings.default_cost_salle_per_day);
        setPortagePct(settings.default_portage_percent);
    };

    const exportPdf = async () => {
        try {
            const inputs = { students, price_per_student: price, cost_formateur: costFormateur, cost_salle: costSalle, portage_percent: portagePct, room_capacity: capacity, formation_slug: formationSlug || null };
            const { data } = await api.post("/admin/previsionnel/scenarios", {
                name: `Export rapide ${new Date().toLocaleDateString("fr-FR")}`,
                description: "Export PDF non sauvegardé en bibliothèque.",
                type: "session", inputs, outputs: out, tags: ["export"],
            });
            const token = localStorage.getItem("cursus_physio_token");
            const r = await fetch(`${API}/admin/previsionnel/scenarios/${data.id}/export.pdf`, { headers: { Authorization: `Bearer ${token}` } });
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = `previsionnel-session-${Date.now()}.pdf`; a.click();
            URL.revokeObjectURL(url);
            toast.success("PDF téléchargé");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="grid gap-6 lg:grid-cols-5">
            {/* Left column — params */}
            <div className="space-y-5 lg:col-span-2" data-testid="prev-session-params">
                <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium">
                    <h3 className="font-display text-xl">Paramètres</h3>
                    <p className="mt-1 text-xs text-brand-smoke">Toutes les valeurs sont éditables — les résultats se recalculent instantanément.</p>
                    <div className="mt-5 space-y-5">
                        <label className="block">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">Formation</span>
                            <select value={formationSlug} onChange={(e) => setFormationSlug(e.target.value)} data-testid="prev-formation"
                                className="mt-1 w-full rounded-xl border border-brand-line bg-white px-3 py-2 outline-none focus:border-brand-orange">
                                <option value="">Sélectionner une formation (auto-prix)</option>
                                {formations.map((f) => <option key={f.slug} value={f.slug}>{f.name} · {f.price_eur}€</option>)}
                            </select>
                        </label>
                        <Input label="Prix / stagiaire (€)" value={price} onChange={(v) => setPrice(Number(v))} testId="prev-price" />
                        <Slider label="Nombre de stagiaires" value={students} min={1} max={capacity} onChange={setStudents} testId="prev-students" />
                        <Input label="Coût formateur (€ / jour)" value={costFormateur} onChange={(v) => setCostFormateur(Number(v))} testId="prev-cost-f" />
                        <Input label="Coût salle (€ / jour)" value={costSalle} onChange={(v) => setCostSalle(Number(v))} testId="prev-cost-s" />
                        <Slider label="% portage Axiome" value={portagePct} min={0} max={30} step={0.5} onChange={setPortagePct} suffix=" %" testId="prev-portage" />
                        <button onClick={reset} className="inline-flex items-center gap-1.5 text-xs text-brand-orange hover:underline" data-testid="prev-reset">
                            <RefreshCw className="h-3 w-3" /> Réinitialiser les défauts
                        </button>
                    </div>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                    <button onClick={() => setSaveOpen(true)} data-testid="prev-save-btn"
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-brand-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#222]">
                        <Save className="h-3.5 w-3.5" /> Sauvegarder ce scénario
                    </button>
                    <button onClick={exportPdf} data-testid="prev-export-btn"
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c]">
                        <FileDown className="h-3.5 w-3.5" /> Exporter en PDF
                    </button>
                </div>
            </div>

            {/* Right column — results */}
            <div className="space-y-5 lg:col-span-3" data-testid="prev-session-results">
                {/* Hero margin */}
                <motion.div
                    key={out.net_margin}
                    initial={{ scale: 0.97, opacity: 0.6 }} animate={{ scale: 1, opacity: 1 }}
                    className={`rounded-3xl p-8 shadow-premium ${
                        status === "ok" ? "bg-gradient-to-br from-brand-orange to-[#d9772c] text-white" :
                        status === "even" ? "bg-gradient-to-br from-brand-sand to-[#efe5d6]" :
                        "bg-gradient-to-br from-red-100 to-red-200"
                    }`}
                >
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider opacity-80">
                        {status === "ok" ? <CheckCircle2 className="h-3.5 w-3.5" /> :
                         status === "even" ? <Scale className="h-3.5 w-3.5" /> :
                         <AlertTriangle className="h-3.5 w-3.5" />}
                        {status === "ok" ? "Bénéficiaire" : status === "even" ? "Point mort" : "Déficitaire"}
                    </div>
                    <div className="mt-4 font-display text-7xl font-medium tracking-tightest" data-testid="prev-net-margin">
                        {fmtSigned(out.net_margin)}
                    </div>
                    <div className="mt-2 text-sm opacity-90">
                        Marge nette · soit <strong>{fmtEUR(out.margin_per_student)} / stagiaire</strong>
                    </div>
                </motion.div>

                {/* 3 auto scenarios */}
                <div className="grid gap-3 sm:grid-cols-3" data-testid="prev-auto-scenarios">
                    {scenarios.map((s) => (
                        <button
                            key={s.key}
                            onClick={() => setStudents(s.students)}
                            data-testid={`prev-scenario-${s.key}`}
                            className="rounded-2xl border border-brand-line bg-white p-4 text-left shadow-sm transition hover:border-brand-orange/60"
                        >
                            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider" style={{ color: s.color }}>
                                <span className="inline-block h-2 w-2 rounded-full" style={{ background: s.color }} />
                                {s.label}
                            </div>
                            <div className="mt-2 text-xs text-brand-smoke">{s.students} stagiaires ({s.pct}% rempli)</div>
                            <div className="mt-1 font-display text-2xl font-medium tracking-tightest text-brand-ink">{fmtSigned(s.net_margin)}</div>
                        </button>
                    ))}
                </div>

                {/* 2x2 mini cards */}
                <div className="grid grid-cols-2 gap-3">
                    <Mini label="CA brut" value={fmtEUR(out.ca_brut)} />
                    <Mini label={`Portage (-${portagePct}%)`} value={"− " + fmtEUR(out.portage)} negative />
                    <Mini label="CA net" value={fmtEUR(out.ca_net)} />
                    <Mini label="Coûts (formateur + salle)" value={"− " + fmtEUR(out.costs_total)} negative />
                </div>

                {/* Break-even card */}
                <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium" data-testid="prev-break-even">
                    <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                            <Target className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">Point mort</div>
                            <div className="mt-1 font-display text-3xl font-medium tracking-tightest">
                                <span className="text-brand-orange">{out.break_even ?? "—"}</span> stagiaires minimum
                            </div>
                            <p className="mt-1 text-xs text-brand-smoke" title="Couvre exactement formateur + salle.">
                                Pour atteindre l'équilibre (couvre formateur + salle après portage Axiome).
                            </p>
                        </div>
                    </div>
                </div>

                {/* Margin curve */}
                <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium" data-testid="prev-margin-chart">
                    <h3 className="font-display text-xl">Marge selon le nombre de stagiaires</h3>
                    <p className="mt-1 text-xs text-brand-smoke">Courbe de marge nette de 1 à {capacity} stagiaires. Le point bleu marque la position courante.</p>
                    <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={marginCurve} margin={{ top: 16, right: 16, left: -8, bottom: 8 }}>
                            <CartesianGrid stroke="#E7E3DC" vertical={false} />
                            <XAxis dataKey="students" fontSize={10} stroke="#8A8A8A" />
                            <YAxis fontSize={10} stroke="#8A8A8A" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                            <Tooltip
                                formatter={(v) => [fmtEUR(v), "Marge nette"]}
                                labelFormatter={(l) => `${l} stagiaires`}
                            />
                            <ReferenceLine y={0} stroke="#dc2626" strokeDasharray="3 3" label={{ value: "Point mort", position: "right", fontSize: 10, fill: "#dc2626" }} />
                            {out.break_even != null && (
                                <ReferenceLine x={out.break_even} stroke="#dc2626" strokeDasharray="3 3" />
                            )}
                            <Line type="monotone" dataKey="margin" stroke="#E8873C" strokeWidth={2.5} dot={(props) => {
                                const { cx, cy, payload } = props;
                                if (payload.students === students) {
                                    return <circle cx={cx} cy={cy} r={6} fill="#0A0A0A" stroke="#fff" strokeWidth={2} />;
                                }
                                return <circle cx={cx} cy={cy} r={2} fill="#E8873C" />;
                            }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Full capacity */}
                <div className="rounded-2xl border border-brand-line bg-brand-sand p-5 shadow-premium" data-testid="prev-full-capacity">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">Si la salle est pleine ({capacity})</div>
                    <div className="mt-2 font-display text-3xl font-medium tracking-tightest text-brand-ink">
                        {fmtSigned(out.full_capacity_margin)}
                    </div>
                    <div className="mt-1 text-xs text-brand-smoke">
                        Marge maximale · {fmtEUR(out.full_capacity_margin / capacity)} par stagiaire
                    </div>
                </div>
            </div>

            <SaveScenarioDialog
                open={saveOpen}
                onClose={() => setSaveOpen(false)}
                defaultName={`Session ${formationSlug || "personnalisée"} · ${students} stagiaires`}
                onSubmit={async (form) => {
                    await onSaveScenario({
                        ...form, type: "session",
                        inputs: { students, price_per_student: price, cost_formateur: costFormateur, cost_salle: costSalle, portage_percent: portagePct, room_capacity: capacity, formation_slug: formationSlug || null },
                        outputs: out,
                    });
                }}
            />
        </div>
    );
}

function Mini({ label, value, negative }) {
    return (
        <div className="rounded-2xl border border-brand-line bg-white p-4 shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">{label}</div>
            <div className={`mt-2 font-display text-2xl font-medium tracking-tightest ${negative ? "text-red-600" : "text-brand-ink"}`}>{value}</div>
        </div>
    );
}

// ================ Sub-tab : Cursus complet ================
function CursusTab({ settings, cursus, formations, onSaveScenario }) {
    const [cursusSlug, setCursusSlug] = useState("");
    const [costFormateur, setCostFormateur] = useState(settings.default_cost_formateur_per_day);
    const [costSalle, setCostSalle] = useState(settings.default_cost_salle_per_day);
    const [portagePct, setPortagePct] = useState(settings.default_portage_percent);
    const capacity = settings.default_room_capacity;
    const [perFormation, setPerFormation] = useState({});  // {slug: students}
    const [saveOpen, setSaveOpen] = useState(false);

    useEffect(() => {
        setCostFormateur(settings.default_cost_formateur_per_day);
        setCostSalle(settings.default_cost_salle_per_day);
        setPortagePct(settings.default_portage_percent);
    }, [settings]);

    const cursusFormations = useMemo(() => formations.filter((f) => f.cursus_slug === cursusSlug), [formations, cursusSlug]);

    useEffect(() => {
        const init = {};
        cursusFormations.forEach((f) => { init[f.slug] = perFormation[f.slug] ?? Math.round(capacity * 0.6); });
        setPerFormation(init);
        // eslint-disable-next-line
    }, [cursusSlug]);

    const applyPreset = (pct) => {
        const next = {}; cursusFormations.forEach((f) => { next[f.slug] = Math.max(1, Math.round(capacity * pct / 100)); });
        setPerFormation(next);
    };

    const rows = useMemo(() => cursusFormations.map((f) => {
        const n = perFormation[f.slug] ?? 0;
        const r = computeSession({ students: n, price: f.price_eur || 390, costFormateur, costSalle, portagePct, capacity });
        return { formation: f, ...r };
    }), [cursusFormations, perFormation, costFormateur, costSalle, portagePct, capacity]);

    const totals = rows.reduce((acc, r) => ({
        ca_brut: acc.ca_brut + r.ca_brut, portage: acc.portage + r.portage, ca_net: acc.ca_net + r.ca_net,
        costs_total: acc.costs_total + r.costs_total, net_margin: acc.net_margin + r.net_margin, students: acc.students + r.students,
    }), { ca_brut: 0, portage: 0, ca_net: 0, costs_total: 0, net_margin: 0, students: 0 });

    const exportPdf = async () => {
        try {
            const { data } = await api.post("/admin/previsionnel/scenarios", {
                name: `Cursus ${cursusSlug} · export ${new Date().toLocaleDateString("fr-FR")}`,
                description: "Export PDF cursus complet.",
                type: "cursus",
                inputs: { cursus_slug: cursusSlug, per_formation: perFormation, cost_formateur: costFormateur, cost_salle: costSalle, portage_percent: portagePct, room_capacity: capacity },
                outputs: { ...totals, rows: rows.map((r) => ({ slug: r.formation.slug, name: r.formation.name, students: r.students, ca_brut: r.ca_brut, net_margin: r.net_margin })) },
                tags: ["cursus", cursusSlug],
            });
            const token = localStorage.getItem("cursus_physio_token");
            const r = await fetch(`${API}/admin/previsionnel/scenarios/${data.id}/export.pdf`, { headers: { Authorization: `Bearer ${token}` } });
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = `previsionnel-cursus-${cursusSlug}.pdf`; a.click();
            URL.revokeObjectURL(url);
            toast.success("PDF cursus téléchargé");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="space-y-6" data-testid="prev-cursus">
            <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium">
                <h3 className="font-display text-xl">Sélection du cursus</h3>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <select value={cursusSlug} onChange={(e) => setCursusSlug(e.target.value)} data-testid="prev-cursus-select"
                        className="rounded-xl border border-brand-line bg-white px-3 py-2 outline-none focus:border-brand-orange">
                        <option value="">Choisir un cursus</option>
                        {cursus.map((c) => <option key={c.slug} value={c.slug}>Cursus {c.name}</option>)}
                    </select>
                    <div className="flex flex-wrap gap-2">
                        {[
                            { k: "cons", label: "Conservateur 35%", pct: 35 },
                            { k: "real", label: "Réaliste 60%", pct: 60 },
                            { k: "opt", label: "Optimiste 95%", pct: 95 },
                        ].map((p) => (
                            <button key={p.k} onClick={() => applyPreset(p.pct)} disabled={!cursusSlug}
                                data-testid={`prev-cursus-preset-${p.k}`}
                                className="rounded-full border border-brand-line bg-white px-3 py-1.5 text-xs hover:border-brand-orange disabled:opacity-50">
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>

                {cursusSlug && cursusFormations.length > 0 && (
                    <div className="mt-6 grid gap-4 lg:grid-cols-3" data-testid="prev-cursus-formations">
                        {cursusFormations.map((f) => (
                            <div key={f.slug} className="rounded-xl border border-brand-line bg-[#FAFAFA] p-4">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-orange">{f.cursus_slug}</div>
                                <div className="mt-1 font-semibold text-brand-ink">{f.name}</div>
                                <div className="mt-1 text-xs text-brand-smoke">Prix : {fmtEUR(f.price_eur || 390)} / stagiaire</div>
                                <div className="mt-3">
                                    <Slider label="Stagiaires" value={perFormation[f.slug] ?? 0} min={1} max={capacity}
                                        onChange={(v) => setPerFormation((s) => ({ ...s, [f.slug]: v }))} testId={`prev-cursus-stud-${f.slug}`} />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {cursusSlug && rows.length > 0 && (
                <>
                <div className="overflow-x-auto rounded-2xl border border-brand-line bg-white shadow-premium" data-testid="prev-cursus-table">
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr className="border-b border-brand-line bg-[#FAFAFA] text-left">
                                <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-brand-smoke">Formation</th>
                                <th className="px-4 py-3 text-right text-[10px] uppercase tracking-wider text-brand-smoke">Stagiaires</th>
                                <th className="px-4 py-3 text-right text-[10px] uppercase tracking-wider text-brand-smoke">CA brut</th>
                                <th className="px-4 py-3 text-right text-[10px] uppercase tracking-wider text-brand-smoke">Portage</th>
                                <th className="px-4 py-3 text-right text-[10px] uppercase tracking-wider text-brand-smoke">Coûts</th>
                                <th className="px-4 py-3 text-right text-[10px] uppercase tracking-wider text-brand-smoke">Marge nette</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-brand-line">
                            {rows.map((r) => (
                                <tr key={r.formation.slug}>
                                    <td className="px-4 py-3 font-semibold">{r.formation.name}</td>
                                    <td className="px-4 py-3 text-right">{r.students}</td>
                                    <td className="px-4 py-3 text-right">{fmtEUR(r.ca_brut)}</td>
                                    <td className="px-4 py-3 text-right text-red-600">− {fmtEUR(r.portage)}</td>
                                    <td className="px-4 py-3 text-right text-red-600">− {fmtEUR(r.costs_total)}</td>
                                    <td className={`px-4 py-3 text-right font-semibold ${r.net_margin >= 0 ? "text-brand-orange" : "text-red-600"}`}>{fmtSigned(r.net_margin)}</td>
                                </tr>
                            ))}
                            <tr className="border-t-2 border-brand-ink bg-brand-sand">
                                <td className="px-4 py-4 font-display text-lg">Total cursus</td>
                                <td className="px-4 py-4 text-right font-semibold">{totals.students}</td>
                                <td className="px-4 py-4 text-right font-semibold">{fmtEUR(totals.ca_brut)}</td>
                                <td className="px-4 py-4 text-right font-semibold text-red-600">− {fmtEUR(totals.portage)}</td>
                                <td className="px-4 py-4 text-right font-semibold text-red-600">− {fmtEUR(totals.costs_total)}</td>
                                <td className={`px-4 py-4 text-right font-display text-2xl ${totals.net_margin >= 0 ? "text-brand-orange" : "text-red-600"}`}>{fmtSigned(totals.net_margin)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                    <Mini label="Marge / stagiaire-cursus complet" value={totals.students > 0 ? fmtEUR(totals.net_margin / Math.max(1, Math.min(...rows.map((r) => r.students)))) : "—"} />
                    <Mini label="CA net total" value={fmtEUR(totals.ca_net)} />
                    <Mini label="Coûts totaux" value={"− " + fmtEUR(totals.costs_total)} negative />
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                    <button onClick={() => setSaveOpen(true)} data-testid="prev-cursus-save"
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-ink px-5 py-2.5 text-sm font-semibold text-white">
                        <Save className="h-3.5 w-3.5" /> Sauvegarder ce cursus
                    </button>
                    <button onClick={exportPdf} data-testid="prev-cursus-export"
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white">
                        <FileDown className="h-3.5 w-3.5" /> Export PDF cursus
                    </button>
                </div>
                </>
            )}

            <SaveScenarioDialog
                open={saveOpen}
                onClose={() => setSaveOpen(false)}
                defaultName={`Cursus ${cursusSlug}`}
                onSubmit={async (form) => {
                    await onSaveScenario({
                        ...form, type: "cursus",
                        inputs: { cursus_slug: cursusSlug, per_formation: perFormation, cost_formateur: costFormateur, cost_salle: costSalle, portage_percent: portagePct, room_capacity: capacity },
                        outputs: { ...totals, rows: rows.map((r) => ({ slug: r.formation.slug, name: r.formation.name, students: r.students, ca_brut: r.ca_brut, net_margin: r.net_margin })) },
                    });
                }}
            />
        </div>
    );
}

// ================ Sub-tab : Annual ================
function AnnualTab({ settings, onSaveScenario }) {
    const [year, setYear] = useState(new Date().getFullYear());
    const [costFormateur, setCostFormateur] = useState(settings.default_cost_formateur_per_day);
    const [costSalle, setCostSalle] = useState(settings.default_cost_salle_per_day);
    const [portagePct, setPortagePct] = useState(settings.default_portage_percent);
    const [price, setPrice] = useState(390);
    const capacity = settings.default_room_capacity;
    const monthsBase = useMemo(() => Array.from({ length: 12 }, (_, i) => ({
        month: `${year}-${String(i + 1).padStart(2, "0")}`,
        sessions: 6,
        students_per_session: 12,
    })), [year]);
    const [months, setMonths] = useState(monthsBase);
    const [saveOpen, setSaveOpen] = useState(false);

    useEffect(() => { setMonths(monthsBase); }, [monthsBase]);
    useEffect(() => {
        setCostFormateur(settings.default_cost_formateur_per_day);
        setCostSalle(settings.default_cost_salle_per_day);
        setPortagePct(settings.default_portage_percent);
    }, [settings]);

    const applyPreset = (preset) => {
        const presets = {
            launch: { sessions: 3, students: 8 },
            cruise: { sessions: 6, students: 12 },
            full: { sessions: 10, students: 16 },
        }[preset];
        if (!presets) return;
        setMonths((m) => m.map((row) => ({ ...row, sessions: presets.sessions, students_per_session: presets.students })));
    };

    const monthly = useMemo(() => months.map((m) => {
        const session = computeSession({ students: m.students_per_session, price, costFormateur, costSalle, portagePct, capacity });
        const sessions = Number(m.sessions) || 0;
        return {
            month: m.month, monthShort: m.month.slice(5),
            sessions, students_per_session: m.students_per_session,
            ca_brut: session.ca_brut * sessions,
            portage: session.portage * sessions,
            ca_net: session.ca_net * sessions,
            costs_total: session.costs_total * sessions,
            net_margin: session.net_margin * sessions,
            total_students: session.students * sessions,
        };
    }), [months, price, costFormateur, costSalle, portagePct, capacity]);

    const cumulative = useMemo(() => {
        let acc = 0;
        return monthly.map((m) => { acc += m.net_margin; return { ...m, cumulative: acc }; });
    }, [monthly]);

    const totals = monthly.reduce((acc, m) => ({
        ca_brut: acc.ca_brut + m.ca_brut, portage: acc.portage + m.portage, ca_net: acc.ca_net + m.ca_net,
        costs_total: acc.costs_total + m.costs_total, net_margin: acc.net_margin + m.net_margin,
        total_sessions: acc.total_sessions + m.sessions, total_students: acc.total_students + m.total_students,
    }), { ca_brut: 0, portage: 0, ca_net: 0, costs_total: 0, net_margin: 0, total_sessions: 0, total_students: 0 });

    const exportPdf = async () => {
        try {
            const inputs = { year, price, cost_formateur: costFormateur, cost_salle: costSalle, portage_percent: portagePct, months };
            const { data } = await api.post("/admin/previsionnel/scenarios", {
                name: `Projection annuelle ${year}`,
                description: `${totals.total_sessions} sessions · ${totals.total_students} stagiaires.`,
                type: "annual", inputs,
                outputs: { ...totals, monthly: monthly.map((m) => ({ month: m.month, sessions: m.sessions, students_per_session: m.students_per_session, ca_brut: m.ca_brut, net_margin: m.net_margin })) },
                tags: ["annual", String(year)],
            });
            const token = localStorage.getItem("cursus_physio_token");
            const r = await fetch(`${API}/admin/previsionnel/scenarios/${data.id}/export.pdf`, { headers: { Authorization: `Bearer ${token}` } });
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = `previsionnel-annuel-${year}.pdf`; a.click();
            URL.revokeObjectURL(url);
            toast.success("PDF projection annuelle téléchargé");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="space-y-6" data-testid="prev-annual">
            <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium">
                <div className="flex flex-wrap items-center gap-3">
                    <div>
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">Année</span>
                        <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="prev-annual-year"
                            className="ml-2 w-28 rounded-xl border border-brand-line bg-white px-3 py-1 outline-none focus:border-brand-orange" />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button onClick={() => applyPreset("launch")} data-testid="prev-annual-preset-launch"
                            className="rounded-full border border-brand-line bg-white px-3 py-1.5 text-xs hover:border-brand-orange">Lancement (3/mois × 8)</button>
                        <button onClick={() => applyPreset("cruise")} data-testid="prev-annual-preset-cruise"
                            className="rounded-full border border-brand-line bg-white px-3 py-1.5 text-xs hover:border-brand-orange">Cruise (6/mois × 12)</button>
                        <button onClick={() => applyPreset("full")} data-testid="prev-annual-preset-full"
                            className="rounded-full border border-brand-line bg-white px-3 py-1.5 text-xs hover:border-brand-orange">Plein régime (10/mois × 16)</button>
                    </div>
                    <div className="ml-auto flex gap-2">
                        <Input label="Prix / stagiaire" value={price} onChange={(v) => setPrice(Number(v))} testId="prev-annual-price" />
                    </div>
                </div>

                <div className="mt-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
                    {months.map((m, i) => (
                        <div key={m.month} className="rounded-xl border border-brand-line bg-[#FAFAFA] p-3">
                            <div className="text-xs font-semibold text-brand-ink">{new Date(year, i, 1).toLocaleDateString("fr-FR", { month: "short" })}</div>
                            <label className="mt-2 block">
                                <span className="text-[10px] uppercase text-brand-smoke">Sessions</span>
                                <input type="number" min={0} max={50} value={m.sessions}
                                    onChange={(e) => setMonths((mm) => mm.map((row, idx) => idx === i ? { ...row, sessions: Number(e.target.value) } : row))}
                                    data-testid={`prev-annual-sessions-${i}`}
                                    className="mt-1 w-full rounded-lg border border-brand-line bg-white px-2 py-1 text-sm outline-none focus:border-brand-orange" />
                            </label>
                            <label className="mt-1 block">
                                <span className="text-[10px] uppercase text-brand-smoke">Stagiaires/session</span>
                                <input type="number" min={1} max={capacity} value={m.students_per_session}
                                    onChange={(e) => setMonths((mm) => mm.map((row, idx) => idx === i ? { ...row, students_per_session: Number(e.target.value) } : row))}
                                    data-testid={`prev-annual-students-${i}`}
                                    className="mt-1 w-full rounded-lg border border-brand-line bg-white px-2 py-1 text-sm outline-none focus:border-brand-orange" />
                            </label>
                        </div>
                    ))}
                </div>
            </div>

            {/* KPIs */}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="prev-annual-kpis">
                <Mini label="CA brut total" value={fmtEUR(totals.ca_brut)} />
                <Mini label={`Portage Axiome (${portagePct}%)`} value={"− " + fmtEUR(totals.portage)} negative />
                <Mini label="CA net" value={fmtEUR(totals.ca_net)} />
                <Mini label="Coûts totaux" value={"− " + fmtEUR(totals.costs_total)} negative />
            </div>

            <motion.div
                key={totals.net_margin}
                initial={{ scale: 0.97 }} animate={{ scale: 1 }}
                className="rounded-3xl bg-gradient-to-br from-brand-orange to-[#d9772c] p-8 text-white shadow-premium"
                data-testid="prev-annual-margin"
            >
                <div className="text-xs font-semibold uppercase tracking-wider opacity-90">Marge nette annuelle {year}</div>
                <div className="mt-3 font-display text-7xl font-medium tracking-tightest">{fmtSigned(totals.net_margin)}</div>
                <div className="mt-2 text-sm opacity-90">
                    <strong>{totals.total_sessions}</strong> sessions · <strong>{totals.total_students}</strong> stagiaires formés
                </div>
            </motion.div>

            <div className="grid gap-5 lg:grid-cols-2">
                <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium" data-testid="prev-annual-monthly-chart">
                    <h3 className="font-display text-xl">CA brut & marge mensuelle</h3>
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={monthly} margin={{ top: 16, right: 16, left: -8, bottom: 8 }}>
                            <CartesianGrid stroke="#E7E3DC" vertical={false} />
                            <XAxis dataKey="monthShort" fontSize={10} stroke="#8A8A8A" />
                            <YAxis fontSize={10} stroke="#8A8A8A" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                            <Tooltip formatter={(v) => fmtEUR(v)} />
                            <Legend />
                            <Bar dataKey="ca_brut" name="CA brut" fill="#0A0A0A" />
                            <Bar dataKey="net_margin" name="Marge nette" fill="#E8873C" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium" data-testid="prev-annual-cum-chart">
                    <h3 className="font-display text-xl">Marge cumulée sur l'année</h3>
                    <ResponsiveContainer width="100%" height={280}>
                        <AreaChart data={cumulative} margin={{ top: 16, right: 16, left: -8, bottom: 8 }}>
                            <CartesianGrid stroke="#E7E3DC" vertical={false} />
                            <XAxis dataKey="monthShort" fontSize={10} stroke="#8A8A8A" />
                            <YAxis fontSize={10} stroke="#8A8A8A" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                            <Tooltip formatter={(v) => fmtEUR(v)} />
                            <Area type="monotone" dataKey="cumulative" stroke="#E8873C" fill="#E8873C" fillOpacity={0.18} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button onClick={() => setSaveOpen(true)} data-testid="prev-annual-save"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-ink px-5 py-2.5 text-sm font-semibold text-white">
                    <Save className="h-3.5 w-3.5" /> Sauvegarder cette projection
                </button>
                <button onClick={exportPdf} data-testid="prev-annual-export"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white">
                    <FileDown className="h-3.5 w-3.5" /> Exporter le prévisionnel annuel
                </button>
            </div>

            <SaveScenarioDialog
                open={saveOpen}
                onClose={() => setSaveOpen(false)}
                defaultName={`Projection annuelle ${year}`}
                onSubmit={async (form) => {
                    await onSaveScenario({
                        ...form, type: "annual",
                        inputs: { year, price, cost_formateur: costFormateur, cost_salle: costSalle, portage_percent: portagePct, months },
                        outputs: { ...totals, monthly: monthly.map((m) => ({ month: m.month, sessions: m.sessions, students_per_session: m.students_per_session, ca_brut: m.ca_brut, net_margin: m.net_margin })) },
                    });
                }}
            />
        </div>
    );
}

// ================ Sub-tab : Saved scenarios + compare ================
function SavedTab({ scenarios, onReload, onDelete, onCompare }) {
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState([]);
    const [comparison, setComparison] = useState(null);
    const filtered = useMemo(() => scenarios.filter((s) => `${s.name} ${(s.tags || []).join(" ")} ${s.description}`.toLowerCase().includes(search.toLowerCase())), [scenarios, search]);
    const toggle = (id) => {
        setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : (s.length >= 3 ? s : [...s, id]));
    };
    const compareNow = async () => {
        try {
            const { data } = await api.post("/admin/previsionnel/compare", { scenario_ids: selected });
            setComparison(data);
            onCompare?.(data);
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const exportOne = async (id) => {
        try {
            const token = localStorage.getItem("cursus_physio_token");
            const r = await fetch(`${API}/admin/previsionnel/scenarios/${id}/export.pdf`, { headers: { Authorization: `Bearer ${token}` } });
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = `previsionnel-${id.slice(0, 8)}.pdf`; a.click();
            URL.revokeObjectURL(url);
        } catch (err) { toast.error("Export PDF impossible"); }
    };

    return (
        <div className="space-y-5" data-testid="prev-saved">
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[220px]">
                    <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-brand-smoke" />
                    <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                        data-testid="prev-saved-search"
                        placeholder="Rechercher par nom, tag ou description"
                        className="w-full rounded-full border border-brand-line bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-brand-orange" />
                </div>
                <button onClick={onReload} className="inline-flex items-center gap-1.5 rounded-full border border-brand-line bg-white px-3 py-1.5 text-xs hover:border-brand-orange" data-testid="prev-saved-reload">
                    <RefreshCw className="h-3 w-3" /> Recharger
                </button>
                <button onClick={compareNow} disabled={selected.length < 2}
                    data-testid="prev-saved-compare"
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-orange px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                    <Scale className="h-3 w-3" /> Comparer ({selected.length})
                </button>
            </div>

            {filtered.length === 0 ? (
                <div className="py-12 text-center text-sm text-brand-smoke">Aucun scénario sauvegardé pour le moment.</div>
            ) : (
                <div className="grid gap-3 md:grid-cols-2">
                    {filtered.map((s) => (
                        <div key={s.id} className={`rounded-2xl border p-4 transition ${
                            selected.includes(s.id) ? "border-brand-orange bg-brand-orange/5" : "border-brand-line bg-white"
                        }`} data-testid={`prev-saved-card-${s.id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="rounded-full bg-brand-line/30 px-2 py-0.5 text-[10px] font-semibold text-brand-smoke">{s.type}</span>
                                        {(s.tags || []).slice(0, 3).map((t) => <span key={t} className="rounded-full bg-brand-orange/10 px-2 py-0.5 text-[10px] text-brand-orange">{t}</span>)}
                                    </div>
                                    <div className="mt-2 font-semibold text-brand-ink">{s.name}</div>
                                    {s.description && <div className="mt-1 text-xs text-brand-smoke line-clamp-2">{s.description}</div>}
                                </div>
                                <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggle(s.id)}
                                    data-testid={`prev-saved-check-${s.id}`} className="mt-1 h-4 w-4 accent-brand-orange" />
                            </div>
                            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                                <Stat label="CA brut" v={fmtEUR(s.outputs?.ca_brut)} />
                                <Stat label="Marge" v={fmtSigned(s.outputs?.net_margin)} highlight={(s.outputs?.net_margin ?? 0) >= 0 ? "ok" : "ko"} />
                                <Stat label="Point mort" v={s.outputs?.break_even ?? "—"} />
                            </div>
                            <div className="mt-3 flex items-center gap-2">
                                <button onClick={() => exportOne(s.id)} title="Export PDF"
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-brand-line text-brand-smoke hover:border-brand-orange hover:text-brand-orange">
                                    <FileDown className="h-3 w-3" />
                                </button>
                                <button onClick={() => onDelete(s.id)} title="Supprimer"
                                    data-testid={`prev-saved-delete-${s.id}`}
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-brand-line text-brand-smoke hover:border-red-500 hover:text-red-500">
                                    <Trash2 className="h-3 w-3" />
                                </button>
                                <span className="ml-auto text-[10px] text-brand-smoke">{(s.created_at || "").slice(0, 10)}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {comparison && (
                <CompareDialog data={comparison} onClose={() => setComparison(null)} />
            )}
        </div>
    );
}

function Stat({ label, v, highlight }) {
    return (
        <div className="rounded-lg bg-[#FAFAFA] p-2">
            <div className="text-[9px] uppercase tracking-wider text-brand-smoke">{label}</div>
            <div className={`mt-0.5 font-semibold ${highlight === "ok" ? "text-brand-orange" : highlight === "ko" ? "text-red-600" : "text-brand-ink"}`}>{v}</div>
        </div>
    );
}

function CompareDialog({ data, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/60 p-4" data-testid="prev-compare-dialog">
            <motion.div initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                className="relative w-full max-w-4xl max-h-[90vh] overflow-auto rounded-3xl bg-white p-8 shadow-premium-lg">
                <button onClick={onClose} className="absolute right-4 top-4 text-brand-smoke"><X className="h-5 w-5" /></button>
                <h3 className="font-display text-2xl">Comparaison</h3>
                <div className="mt-6 overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr className="border-b border-brand-line text-left">
                                <th className="px-3 py-3 text-[10px] uppercase text-brand-smoke">Indicateur</th>
                                {data.scenarios.map((s) => (
                                    <th key={s.id} className="px-3 py-3 text-[10px] uppercase text-brand-smoke">{s.name}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-brand-line">
                            {data.table.map((row) => (
                                <tr key={row.key}>
                                    <td className="px-3 py-3 font-semibold text-brand-ink">{row.label}</td>
                                    {row.values.map((v, i) => (
                                        <td key={i} className={`px-3 py-3 ${row.best_index === i ? "bg-brand-orange/10 font-semibold text-brand-orange" : ""}`}>
                                            {typeof v === "number"
                                                ? (row.key === "students" || row.key === "break_even" ? v : fmtEUR(v))
                                                : (v ?? "—")}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="mt-3 text-[10px] text-brand-smoke">Cellules en orange = meilleure valeur (ou plus basse pour le point mort).</div>
            </motion.div>
        </div>
    );
}

// ================ Main page ================
export default function AdminPrevisionnel() {
    const [tab, setTab] = useState("session");
    const [settings, setSettings] = useState({
        default_cost_formateur_per_day: 1000, default_cost_salle_per_day: 500,
        default_portage_percent: 15, default_room_capacity: 20,
    });
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [formations, setFormations] = useState([]);
    const [cursus, setCursus] = useState([]);
    const [scenarios, setScenarios] = useState([]);

    const reloadScenarios = async () => {
        try {
            const { data } = await api.get("/admin/previsionnel/scenarios");
            setScenarios(data || []);
        } catch (err) { /* silent */ }
    };

    useEffect(() => {
        api.get("/admin/previsionnel/settings").then((r) => setSettings(r.data)).catch(() => {});
        api.get("/formations").then((r) => setFormations(r.data || [])).catch(() => {});
        api.get("/cursus").then((r) => setCursus(r.data || [])).catch(() => {});
        reloadScenarios();
    }, []);

    const handleSaveScenario = async (payload) => {
        try {
            await api.post("/admin/previsionnel/scenarios", payload);
            toast.success(`Scénario "${payload.name}" sauvegardé`);
            reloadScenarios();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer ce scénario ?")) return;
        try {
            await api.delete(`/admin/previsionnel/scenarios/${id}`);
            toast.success("Scénario supprimé");
            reloadScenarios();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const TABS = [
        { key: "session", label: "Session unique", icon: Sparkles },
        { key: "cursus", label: "Cursus complet", icon: Layers },
        { key: "annual", label: "Projection annuelle", icon: TrendingUp },
        { key: "saved", label: `Scénarios sauvegardés (${scenarios.length})`, icon: BarChart2 },
    ];

    return (
        <div className="mx-auto max-w-7xl px-6 py-10" data-testid="page-admin-previsionnel">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">Pilotage financier</div>
                    <h1 className="mt-2 font-display text-5xl font-normal tracking-tightest">Prévisionnel · Simulateur de rentabilité</h1>
                    <p className="mt-2 max-w-2xl text-brand-smoke">Simulez vos sessions, cursus et année — point mort, marges, scénarios.</p>
                </div>
                <button onClick={() => setSettingsOpen(true)} data-testid="prev-open-settings"
                    className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-2 text-sm hover:border-brand-orange">
                    <SettingsIcon className="h-3.5 w-3.5" /> Paramètres
                </button>
            </div>

            <div className="mt-8 flex flex-wrap gap-2 border-b border-brand-line" data-testid="prev-subtabs">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`prev-subtab-${t.key}`}
                            className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold transition border-b-2 -mb-px ${
                                active ? "border-brand-orange text-brand-orange" : "border-transparent text-brand-smoke hover:text-brand-ink"
                            }`}
                        >
                            <Icon className="h-3.5 w-3.5" /> {t.label}
                        </button>
                    );
                })}
            </div>

            <div className="mt-8" data-testid={`prev-content-${tab}`}>
                <AnimatePresence mode="wait">
                    <motion.div key={tab}
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}>
                        {tab === "session" && <SessionTab settings={settings} formations={formations} onSaveScenario={handleSaveScenario} />}
                        {tab === "cursus" && <CursusTab settings={settings} cursus={cursus} formations={formations} onSaveScenario={handleSaveScenario} />}
                        {tab === "annual" && <AnnualTab settings={settings} onSaveScenario={handleSaveScenario} />}
                        {tab === "saved" && <SavedTab scenarios={scenarios} onReload={reloadScenarios} onDelete={handleDelete} />}
                    </motion.div>
                </AnimatePresence>
            </div>

            <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)}
                settings={settings} onSaved={(s) => setSettings(s)} />
        </div>
    );
}
